import { dateFromISO, dateToISO } from "@xflr6/utils";
import React, { ReactElement, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { DateRange } from "../../components/forms/DateRange";
import LoadingLayout, {
  ErrorLayout,
  NoResultsLayout,
} from "../../components/LoadingLayout";
import Tabs from "../../components/Tabs";
import PromptAnalyticsView from "../../featuresCommon/stats/PromptAnalyticsView";
import {
  FlowStatsFilter,
  loadStats,
  selectFilter,
  selectStatsState,
} from "./flowStatsSlice";
import styles from "./FlowStatsView.module.css";
import PathAnalyticsView from "./PathAnalyticsView";
import ResponsesView from "./ResponsesView";

export const PATHS_TAB = "Paths";
export const PROMPTS_TAB = "Summary";
export const RESPONSES_TAB = "Chats";
const TABS = [PROMPTS_TAB, PATHS_TAB, RESPONSES_TAB];

const LS_FLOW_STATS_TAB_KEY = "flowStatsTab";

export function primeTab(tabName: string): void {
  localStorage.setItem(LS_FLOW_STATS_TAB_KEY, tabName);
}

function FlowStatsView({ flowId }: { flowId: number }): ReactElement | null {
  const dispatch = useDispatch();
  const filter = useSelector(selectFilter);
  const statsState = useSelector(selectStatsState);

  const [activeTab, setActiveTab] = useState(
    localStorage.getItem(LS_FLOW_STATS_TAB_KEY) ?? TABS[0]
  );

  useEffect(() => {
    primeTab(activeTab);
  }, [activeTab]);

  const loadStats_ = useMemo(
    () => (filter?: FlowStatsFilter) => {
      dispatch(loadStats({ flowId, filter }));
    },
    [dispatch, flowId]
  );

  useEffect(() => {
    loadStats_();
  }, [loadStats_, dispatch]);

  function renderFilters(): ReactElement {
    return (
      <div className={styles.filters}>
        from&nbsp;
        <DateRange
          pickerProps={{
            calendarIcon: null,
          }}
          value={{
            from: dateFromISO(filter.fromDate),
            till: dateFromISO(filter.tillDate),
          }}
          onChange={(value) => {
            loadStats_({
              fromDate: dateToISO(value.from),
              tillDate: dateToISO(value.till),
            });
          }}
        />
      </div>
    );
  }

  function renderTabBody(): ReactElement | null {
    if (statsState.loading === "pending") {
      return <LoadingLayout />;
    } else if (statsState.loading === "rejected") {
      return <ErrorLayout message={statsState.loadError} />;
    } else if (statsState.stats == null) {
      return null;
    } else if (statsState.stats.total === 0) {
      return <NoResultsLayout message="There are no chats" />;
    } else if (activeTab === PATHS_TAB) {
      return <PathAnalyticsView flowStats={statsState.stats} />;
    } else if (activeTab === PROMPTS_TAB) {
      return (
        <PromptAnalyticsView flowStats={statsState.stats} showBigPicture />
      );
    } else if (activeTab === RESPONSES_TAB) {
      return <ResponsesView flowId={flowId} />;
    }
    return null;
  }

  return (
    <div className={styles.root}>
      <Tabs
        className={styles.tabs}
        tabs={TABS}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        trailing={renderFilters()}
      />
      <section className={styles.body}>{renderTabBody()}</section>
    </div>
  );
}

export default FlowStatsView;
